import {
  FC,
  KeyboardEvent,
  ReactNode,
  RefObject,
  useEffect,
  useState,
} from "react";
import clsx from "classnames";
import { TextField } from "../../core/TextField/TextField";
import { TVKeyboard } from "../TVKeyboard/TVKeyboard";
import { useAppSelector } from "../../hooks";
import { selectIsDesktopView } from "../../../store/slices/appState";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { isGamepadMode } from "../../../hooks/useCrossInput";
import { isTV } from "../../../utils/platform";
import { useStyles } from "./OtpInput.style";

interface IOtpInputProps {
  /** Number of boxes */
  length: number;
  digits: string[];
  onChange: (digits: string[]) => void;
  /** Each box is a gamepad stop: `${gamepadPrefix}-${index}` */
  gamepadPrefix: string;
  ariaLabel: string;
  isError?: boolean;
  isMasked?: boolean;
  /** The dialog the TV keyboard opens in */
  keyboardPortalRef?: RefObject<Element | null>;
  /** Rendered under the boxes, aligned to their edges */
  children?: ReactNode;
  onEnter?: () => void;
}

export const OtpInput: FC<IOtpInputProps> = ({
  length,
  digits,
  onChange,
  gamepadPrefix,
  ariaLabel,
  isError,
  isMasked,
  keyboardPortalRef,
  children,
  onEnter,
}) => {
  const classes = useStyles();
  const isDesktop = useAppSelector(selectIsDesktopView);
  const { activeElement, setActiveElement } = useGamepadContext();
  const [keyboardIndex, setKeyboardIndex] = useState<number | null>(null);
  const isLarge = isDesktop || isTV;

  useEffect(() => {
    if (isTV) return;
    if (!activeElement?.getAttribute("data-gamepad")?.startsWith(gamepadPrefix))
      return;

    activeElement.querySelector<HTMLInputElement>("input")?.focus();
  }, [activeElement, gamepadPrefix]);

  const setDigit = (index: number, digit: string) => {
    const next = Array.from({ length }, (_, i) => digits[i] ?? "");
    next[index] = digit;
    onChange(next);
  };

  const focusBox = (index: number) => {
    const box = document.querySelector<HTMLElement>(
      `[data-gamepad="${gamepadPrefix}-${index}"]`,
    );
    if (!box) return;
    if (isGamepadMode() || isTV) {
      setActiveElement(box);
    }
    if (isTV) {
      box.click();
    } else {
      box.querySelector<HTMLInputElement>("input")?.focus();
    }
  };

  const updateDigit = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(0, 1);
    setDigit(index, digit);
    if (digit && !isTV && index < length - 1) {
      requestAnimationFrame(() => focusBox(index + 1));
    }
  };

  const onKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      updateDigit(index, event.key);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      onEnter?.();
      return;
    }

    if (event.key !== "Backspace" && event.key !== "Delete") return;

    event.preventDefault();
    if (digits[index]) {
      setDigit(index, "");
      return;
    }

    if (index > 0) {
      setDigit(index - 1, "");
      focusBox(index - 1);
    }
  };

  const openBox = (index: number) => {
    if (isTV) {
      setKeyboardIndex(index);
      return;
    }

    focusBox(index);
  };

  const onKeyboardChange = (value: string) => {
    if (keyboardIndex === null) return;

    const digit = value.replace(/\D/g, "").slice(0, 1);
    if (!digit) {
      const targetIndex = digits[keyboardIndex]
        ? keyboardIndex
        : keyboardIndex - 1;
      if (targetIndex < 0) return;

      setDigit(targetIndex, "");
      setKeyboardIndex(targetIndex);
      return;
    }

    setDigit(keyboardIndex, digit);
    if (keyboardIndex < length - 1) setKeyboardIndex(keyboardIndex + 1);
  };

  const onKeyboardKey = (key: string) => {
    if (key === "Backspace") {
      onKeyboardChange("");
    } else if (key === "Enter") {
      onEnter?.();
    } else if (/^\d$/.test(key)) {
      onKeyboardChange(key);
    }
  };

  return (
    <>
      <div
        className={clsx(classes.row, isLarge && classes.rowLarge)}
        style={{ maxWidth: length * (isLarge ? 82 : 67) }}
      >
        <div
          className={clsx(classes.boxes, isLarge && classes.boxesLarge)}
          style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}
        >
          {Array.from({ length }, (_, index) => (
            <TextField
              key={index}
              centerInput
              maxLength={1}
              onChangeValue={(value) => updateDigit(index, value)}
              useTVKeyboard={false}
              onClick={() => openBox(index)}
              className={clsx(
                classes.box,
                isError && classes.boxError,
                keyboardIndex === index && classes.selected,
              )}
              inputProps={{
                "aria-label": `${ariaLabel} ${index + 1}`,
                autoComplete: index === 0 ? "one-time-code" : "off",
                className: classes.input,
                dataGamepad: `${gamepadPrefix}-${index}`,
                inputMode: "numeric",
                onKeyDown: (event) => onKeyDown(index, event),
                onMouseDown: (event) => {
                  if (isTV) event.preventDefault();
                },
                style: { textAlign: "center" },
                value: digits[index] ?? "",
                type: isMasked ? "password" : "text",
              }}
            />
          ))}
        </div>
        {children}
      </div>
      {isTV && keyboardIndex !== null && (
        <TVKeyboard
          open
          portalTarget={keyboardPortalRef?.current}
          value={digits[keyboardIndex]}
          onChange={onKeyboardChange}
          onSendKey={onKeyboardKey}
          onClose={() => setKeyboardIndex(null)}
          replaceOnFirstInput={Boolean(digits[keyboardIndex])}
          restoreGamepadTarget={`${gamepadPrefix}-${keyboardIndex}`}
        />
      )}
    </>
  );
};
